import { useMemo } from 'react';

import type { OperationHistoryEvent } from '../OperationHistory.types';

export interface EventGroup<
  TEvent extends OperationHistoryEvent = OperationHistoryEvent,
> {
  id: string;
  events: TEvent[];
  timestamp: Date;
  authorName: string;
  authorAvatarUrl: string | null;
  isGrouped: boolean;
}

export interface UseOperationHistoryGroupingResult<
  TEvent extends OperationHistoryEvent = OperationHistoryEvent,
> {
  groupedEvents: EventGroup<TEvent>[];
  totalGroups: number;
}

function sortByNewestFirst<TEvent extends OperationHistoryEvent>(
  events: TEvent[]
): TEvent[] {
  return [...events].sort(
    (eventA, eventB) =>
      new Date(eventB.createdAt).getTime() -
      new Date(eventA.createdAt).getTime()
  );
}

function collectBatchGroups<TEvent extends OperationHistoryEvent>(
  sortedEvents: TEvent[]
): Map<string, TEvent[]> {
  const batchGroups = new Map<string, TEvent[]>();

  for (const event of sortedEvents) {
    const batchId = event.metadata?.batchId;
    if (batchId && typeof batchId === 'string') {
      const existing = batchGroups.get(batchId);
      if (existing) {
        existing.push(event);
      } else {
        batchGroups.set(batchId, [event]);
      }
    }
  }

  return batchGroups;
}

function createBatchEventGroups<TEvent extends OperationHistoryEvent>(
  batchGroups: Map<string, TEvent[]>,
  processedEventIds: Set<string>
): EventGroup<TEvent>[] {
  const groups: EventGroup<TEvent>[] = [];

  for (const [batchId, batchEvents] of batchGroups.entries()) {
    if (batchEvents.length <= 1) continue;

    const sortedBatchEvents = sortByNewestFirst(batchEvents);
    const firstEvent = sortedBatchEvents[0];

    if (!firstEvent) continue;

    groups.push({
      id: `batch-${batchId}`,
      events: sortedBatchEvents,
      timestamp: new Date(firstEvent.createdAt),
      authorName: firstEvent.authorName,
      authorAvatarUrl: firstEvent.authorAvatarUrl,
      isGrouped: true,
    });

    for (const event of batchEvents) {
      processedEventIds.add(event.id);
    }
  }

  return groups;
}

export function useOperationHistoryGrouping<
  TEvent extends OperationHistoryEvent,
>(events: TEvent[]): UseOperationHistoryGroupingResult<TEvent> {
  const groupedEvents = useMemo((): EventGroup<TEvent>[] => {
    if (events.length === 0) {
      return [];
    }

    const sortedEvents = sortByNewestFirst(events);
    const processedEventIds = new Set<string>();

    const batchGroups = collectBatchGroups(sortedEvents);
    const groups = createBatchEventGroups(batchGroups, processedEventIds);

    for (const event of sortedEvents) {
      if (processedEventIds.has(event.id)) continue;

      groups.push({
        id: `single-${event.id}`,
        events: [event],
        timestamp: new Date(event.createdAt),
        authorName: event.authorName,
        authorAvatarUrl: event.authorAvatarUrl,
        isGrouped: false,
      });
    }

    return groups.sort(
      (groupA, groupB) =>
        groupB.timestamp.getTime() - groupA.timestamp.getTime()
    );
  }, [events]);

  const totalGroups = groupedEvents.length;

  return {
    groupedEvents,
    totalGroups,
  };
}
